import { CommandResult, FormApiService, ItemCommandResult, ODataResult, QueryApiService } from 'src/common/api-service';
import { COMPLETION_SELECT } from 'src/api/profile-completion';
import { Expand, Filter, QueryOptions, Select } from 'odata-query';
import {
  PersonProfile,
  UpsertPersonProfileExperiencesBody,
  UpsertPersonProfileExperiencesResult,
  UpsertPersonProfileJobRoleSkillsBody,
  UpsertPersonProfileJobRolesBody,
  UpsertPersonProfileJobRolesResult,
  UpsertPersonProfileJobSkillsResult,
  UpsertPersonProfileLanguagesBody,
  UpsertPersonProfileLanguagesResult,
  UpsertPersonProfileMethodologiesBody,
  UpsertPersonProfileMethodologiesResult,
  UpsertPersonProfileServiceInfoBody,
  UpsertPersonProfileServiceInfoResult,
  AddProfessionalExperienceBody,
  UpdateProfessionalExperienceBody,
  ProfessionalExperienceDto,
  AddEducationalQualificationBody,
  EducationalQualificationDto,
  UpdateEducationalQualificationBody,
  ReorderEducationalQualificationsBody,
  EducationalQualificationOrder,
  AddCertificationBody,
  CertificationDto,
  UpdateCertificationBody,
  AvailabilitySlot,
  CreateAvailabilitySlotBody,
  AvailabilitySlotDto,
  MeetingAvailabilitySlot,
  MeetingAvailabilitySlotDto,
  UpsertMeetingAvailabilitySlotBody,
  UpsertPersonProfileContactAndProfessionalInfoBody,
  UpsertPersonProfileContactAndProfessionalInfoResult,
  CertificationOrder,
  ReorderCertificationsBody,
  ProfessionalExperienceOrder,
  ReorderProfessionalExperiencesBody,
  PersonMeetingAvailabilityInfo,
  PersonReview,
  PersonCancelAccountReason,
  UpsertPersonCvBody,
  UpsertPersonCvDocumentsResponse,
  ProfileSection
} from './types';
import axios, { AxiosError } from 'axios';
import { AvailabilitySlotNotificationFrequency } from './types';
import { BadResponseFormatError } from 'src/shared/configs/axios-config';
import { uploadFiles } from 'src/common/upload-file';
import i18n from 'src/i18n';
import { splitFileInfo } from 'src/common/contracting-info';

export interface CaptationSuggestedRole {
  roleId: string;
  name: string;
}

export class PersonProfileApiService extends QueryApiService<PersonProfile> {
  constructor() {
    super('personProfiles');
  }

  public getMyProfile(
    options: { select?: Select<PersonProfile>; expand?: Expand<PersonProfile> } = {
      select: [
        'id',
        'userIdentityId',
        'domainId',
        'nickName',
        'fullName',
        'publicFullName',
        'personalUrl',
        'publicPersonalUrl',
        'phoneNumber',
        'serviceDefinition',
        'serviceDefinitionPhrase',
        'workLocation',
        'profileImage',
        'meetingAvailabilitySlot',
        'availabilitySlots',
        'showEnterpriseNameOnProfessionalExperiences',
        'ratingAverages',
        'receiveEmailNotifications',
        'cancellationRequestStatus',
        'rejectedCancellationRequestComment',
        'onboardingCompleted',
        'cvDocumentFileId',
        'professionalExperienceCompleted',
        'educationalQualificationsCompleted',
        'certificationCompleted',
        'linkedInUrl',
        ...COMPLETION_SELECT
      ],
      expand: {
        cvDocumentFile: {
          select: ['id', 'fileName', 'mimeType']
        },
        contactAgent: {
          select: ['id', 'email', 'fullName', 'phoneNumber', 'bannerImageFileId', 'bannerImageFile', 'meetingSchedulingUrl']
        },
        roles: {
          select: ['roleId', 'order', 'experience', 'favorite'],
          expand: {
            role: {
              select: ['id', 'name']
            }
          }
        },
        methodologies: {
          select: ['methodologyId', 'experience', 'favorite', 'order'],
          expand: {
            methodology: {
              select: ['id', 'name']
            }
          }
        },
        languages: {
          select: ['level', 'order'],
          expand: {
            language: {
              select: ['id', 'name']
            }
          }
        },
        skills: {
          select: ['roleId', 'experience', 'favorite', 'skillId', 'order'],
          expand: {
            skill: {
              select: ['id', 'name']
            }
          }
        },
        professionalExperiences: {
          select: [
            'id',
            'role',
            'description',
            'enterpriseOrClient',
            'publicEnterpriseOrClient',
            'from',
            'to',
            'onGoing',
            'location',
            'projectName',
            'relatedResourceLabel',
            'relatedResourcePassword',
            'relatedResourceUrl',
            'order'
          ]
        },
        educationalQualifications: {
          select: ['id', 'title', 'institution', 'onGoing', 'from', 'to', 'order']
        },
        certifications: {
          select: ['id', 'title', 'institution', 'onGoing', 'date', 'url', 'order']
        },
        meetingAvailabilitySlot: {},
        availabilitySlots: {},
        userIdentity: {
          select: ['id', 'email', 'displayName', 'emailValidated']
        }
      }
    }
  ): Promise<PersonProfile> {
    return this.getOneWithOData(options, { $useSplitQuery: true }, 'getMyProfile');
  }

  public async getPublicName(personProfileId: string): Promise<string> {
    try {
      const result = await this.get<{ publicName: string }>(`${personProfileId}/publicName`);
      return result?.publicName ?? '';
    } catch {
      return '';
    }
  }

  public getOne(
    id: string,
    options: { select?: Select<PersonProfile>; expand?: Expand<PersonProfile> } = {
      select: [
        'id',
        'userIdentityId',
        'domainId',
        'nickName',
        'fullName',
        'publicFullName',
        'personalUrl',
        'publicPersonalUrl',
        'phoneNumber',
        'serviceDefinition',
        'serviceDefinitionPhrase',
        'workLocation',
        'profileImage',
        'meetingAvailabilitySlot',
        'showEnterpriseNameOnProfessionalExperiences',
        'ratingAverages',
        'receiveEmailNotifications'
      ],
      expand: {
        contactAgent: {
          select: ['id', 'email', 'fullName', 'phoneNumber', 'bannerImageFileId', 'bannerImageFile']
        },
        roles: {
          select: ['roleId', 'order', 'experience', 'favorite'],
          expand: {
            role: {
              select: ['id', 'name']
            }
          }
        },
        methodologies: {
          select: ['methodologyId', 'experience', 'favorite', 'order'],
          expand: {
            methodology: {
              select: ['id', 'name']
            }
          }
        },
        languages: {
          select: ['level', 'order'],
          expand: {
            language: {
              select: ['id', 'name']
            }
          }
        },
        skills: {
          select: ['roleId', 'experience', 'favorite', 'skillId', 'order'],
          expand: {
            skill: {
              select: ['id', 'name', 'isDraft']
            }
          }
        },
        professionalExperiences: {
          select: [
            'id',
            'role',
            'description',
            'enterpriseOrClient',
            'publicEnterpriseOrClient',
            'from',
            'to',
            'onGoing',
            'location',
            'projectName',
            'relatedResourceLabel',
            'relatedResourcePassword',
            'relatedResourceUrl',
            'order'
          ]
        },
        educationalQualifications: {
          select: ['id', 'title', 'institution', 'onGoing', 'from', 'to', 'order']
        },
        certifications: {
          select: ['id', 'title', 'institution', 'onGoing', 'date', 'url', 'order']
        },
        meetingAvailabilitySlot: {}
      }
    },
    params?: { [key: string]: any }
  ): Promise<PersonProfile> {
    return this.getById(id, options, params);
  }

  public getAvailabilitySlots(
    profileId: string,
    options?: Partial<QueryOptions<AvailabilitySlot>>,
    params?: { [key: string]: any }
  ): Promise<ODataResult<AvailabilitySlot>> {
    return this.getWithOData(options, params, `${encodeURIComponent(profileId)}/availabilitySlots`);
  }

  public getAvailabilitySlot(
    profileId: string,
    slotId: string,
    options?: Partial<QueryOptions<AvailabilitySlot>>,
    params?: { [key: string]: any }
  ): Promise<AvailabilitySlot> {
    return this.getOneWithOData(options, params, `${encodeURIComponent(profileId)}/availabilitySlot(key2=${encodeURIComponent(slotId)})`);
  }

  public async getMeetingAvailabilitySlot(
    profileId: string,
    options?: {
      select: Select<MeetingAvailabilitySlot>;
      filter: Filter<MeetingAvailabilitySlot>;
      expand: Expand<MeetingAvailabilitySlot>;
    },
    params?: { [key: string]: any }
  ): Promise<MeetingAvailabilitySlot | null> {
    try {
      // TODO CHECK THIS
      return this.getOneWithOData(options, params, `${encodeURIComponent(profileId)}/meetingAvailabilitySlot`, true);
    } catch (e) {
      // when 404 but backend return index.html
      if (e instanceof BadResponseFormatError) return null;

      const err = e as AxiosError;
      if (err?.response?.status === 404) return null;

      throw e;
    }
  }

  public async getMeetingAvailabilityInfo(
    profileId: string,
    params?: { projectId?: string; from?: string; to?: string }
  ): Promise<PersonMeetingAvailabilityInfo | null> {
    // TODO: the `await` is missing, so this catch never runs and every status reaches the caller as a
    // rejection. Both callers read the server's reason off the AxiosError because of it.
    try {
      return this.get(`${encodeURIComponent(profileId)}/meetingAvailabilityInfo`, params);
    } catch (e) {
      // when 404 but backend return index.html
      if (e instanceof BadResponseFormatError) return null;

      const err = e as AxiosError;

      if (err?.response?.status === 404) return null;
      else if (err?.response?.status === 409 && err.response.data != null) return err.response.data as PersonMeetingAvailabilityInfo;

      throw e;
    }
  }

  public async getReviews(
    profileId: string,
    options?: { select?: Select<PersonReview>; expand?: Expand<PersonReview>; count?: boolean; top?: number; skip?: number },
    params?: { [key: string]: any }
  ): Promise<ODataResult<PersonReview>> {
    return this.getWithOData(options, params, `${encodeURIComponent(profileId)}/reviews`);
  }

  public async getSuggestedCaptationRoles(): Promise<CaptationSuggestedRole[]> {
    const result = await this.get<CaptationSuggestedRole[]>('suggested-captation-roles');
    return result ?? [];
  }
}

export class PersonProfileFormApiService extends FormApiService {
  constructor() {
    super('personProfiles');
  }

  public async setOnboardingCompleted(profileId: string): Promise<CommandResult> {
    try {
      return await this.post(null, `${encodeURIComponent(profileId)}/onboardingCompleted`);
    } catch (e) {
      const axiosError = e as AxiosError<CommandResult>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async upsertJobRoles(body: UpsertPersonProfileJobRolesBody): Promise<ItemCommandResult<UpsertPersonProfileJobRolesResult>> {
    try {
      return await this.put(body, 'upsertJobRoles');
    } catch (e) {
      const axiosError = e as AxiosError<ItemCommandResult<UpsertPersonProfileJobRolesResult>>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async upsertJobSkills(body: UpsertPersonProfileJobRoleSkillsBody): Promise<ItemCommandResult<UpsertPersonProfileJobSkillsResult>> {
    try {
      return await this.put(body, 'upsertJobSkills');
    } catch (e) {
      const axiosError = e as AxiosError<ItemCommandResult<UpsertPersonProfileJobSkillsResult>>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async upsertMethodologies(body: UpsertPersonProfileMethodologiesBody): Promise<ItemCommandResult<UpsertPersonProfileMethodologiesResult>> {
    try {
      return await this.put(body, 'upsertMethodologies');
    } catch (e) {
      const axiosError = e as AxiosError<ItemCommandResult<UpsertPersonProfileMethodologiesResult>>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async upsertExperiences(body: UpsertPersonProfileExperiencesBody): Promise<ItemCommandResult<UpsertPersonProfileExperiencesResult>> {
    try {
      return await this.put(body, 'upsertExperiences');
    } catch (e) {
      const axiosError = e as AxiosError<ItemCommandResult<UpsertPersonProfileExperiencesResult>>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async upsertLanguages(body: UpsertPersonProfileLanguagesBody): Promise<ItemCommandResult<UpsertPersonProfileLanguagesResult>> {
    try {
      return await this.put(body, 'upsertLanguages');
    } catch (e) {
      const axiosError = e as AxiosError<ItemCommandResult<UpsertPersonProfileLanguagesResult>>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async upsertServiceInfo(body: UpsertPersonProfileServiceInfoBody): Promise<ItemCommandResult<UpsertPersonProfileServiceInfoResult>> {
    try {
      return await this.put(body, 'upsertServiceInfo');
    } catch (e) {
      const axiosError = e as AxiosError<ItemCommandResult<UpsertPersonProfileServiceInfoResult>>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async upsertContactAndProfessionalInfo(
    body: UpsertPersonProfileContactAndProfessionalInfoBody
  ): Promise<ItemCommandResult<UpsertPersonProfileContactAndProfessionalInfoResult>> {
    try {
      return await this.put(body, 'upsertContactAndProfessionalInfo');
    } catch (e) {
      const axiosError = e as AxiosError<ItemCommandResult<UpsertPersonProfileContactAndProfessionalInfoResult>>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async upsertPhoneNumber(profileId: string, body: { phoneNumber: string; phoneNumberCountryId: number }): Promise<CommandResult> {
    try {
      return await this.put(body, `${encodeURIComponent(profileId)}/upsertPhoneNumber`);
    } catch (e) {
      const axiosError = e as AxiosError<CommandResult>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async addProfessionalExperience(body: AddProfessionalExperienceBody): Promise<ItemCommandResult<ProfessionalExperienceDto>> {
    try {
      return await this.post(body, 'addProfessionalExperience');
    } catch (e) {
      const axiosError = e as AxiosError<ItemCommandResult<ProfessionalExperienceDto>>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async updateProfessionalExperience(body: UpdateProfessionalExperienceBody): Promise<ItemCommandResult<ProfessionalExperienceDto>> {
    try {
      return await this.put(body, 'updateProfessionalExperience');
    } catch (e) {
      const axiosError = e as AxiosError<ItemCommandResult<ProfessionalExperienceDto>>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async deleteProfessionalExperience(profileId: string, professionalExperienceId: string): Promise<CommandResult> {
    try {
      const result = await axios.request<CommandResult>({
        url: `${this._apiBaseUrl}/deleteProfessionalExperience`,
        data: { profileId, professionalExperienceId },
        method: 'DELETE'
      });
      return result?.data;
    } catch (e) {
      const axiosError = e as AxiosError<CommandResult>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async reorderProfessionalExperiences(body: ReorderProfessionalExperiencesBody): Promise<ItemCommandResult<ProfessionalExperienceOrder[]>> {
    try {
      return await this.put(body, 'reorderProfessionalExperiences');
    } catch (e) {
      const axiosError = e as AxiosError<ItemCommandResult<ProfessionalExperienceOrder[]>>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async addEducationalQualification(body: AddEducationalQualificationBody): Promise<ItemCommandResult<EducationalQualificationDto>> {
    try {
      return await this.post(body, 'addEducationalQualification');
    } catch (e) {
      const axiosError = e as AxiosError<ItemCommandResult<EducationalQualificationDto>>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async updateEducationalQualification(body: UpdateEducationalQualificationBody): Promise<ItemCommandResult<EducationalQualificationDto>> {
    try {
      return await this.put(body, 'updateEducationalQualification');
    } catch (e) {
      const axiosError = e as AxiosError<ItemCommandResult<EducationalQualificationDto>>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async reorderEducationalQualifications(
    body: ReorderEducationalQualificationsBody
  ): Promise<ItemCommandResult<EducationalQualificationOrder[]>> {
    try {
      return await this.put(body, 'reorderEducationalQualifications');
    } catch (e) {
      const axiosError = e as AxiosError<ItemCommandResult<EducationalQualificationOrder[]>>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async deleteEducationalQualification(profileId: string, educationalQualificationId: string): Promise<CommandResult> {
    try {
      const result = await axios.request<CommandResult>({
        url: `${this._apiBaseUrl}/deleteEducationalQualification`,
        data: { personProfileId: profileId, id: educationalQualificationId },
        method: 'DELETE'
      });
      return result?.data;
    } catch (e) {
      const axiosError = e as AxiosError<CommandResult>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async addCertification(body: AddCertificationBody): Promise<ItemCommandResult<CertificationDto>> {
    try {
      return await this.post(body, 'addCertification');
    } catch (e) {
      const axiosError = e as AxiosError<ItemCommandResult<CertificationDto>>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async updateCertification(body: UpdateCertificationBody): Promise<ItemCommandResult<CertificationDto>> {
    try {
      return await this.put(body, 'updateCertification');
    } catch (e) {
      const axiosError = e as AxiosError<ItemCommandResult<CertificationDto>>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async reorderCertifications(body: ReorderCertificationsBody): Promise<ItemCommandResult<CertificationOrder[]>> {
    try {
      return await this.put(body, 'reorderCertifications');
    } catch (e) {
      const axiosError = e as AxiosError<ItemCommandResult<CertificationOrder[]>>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async deleteCertification(profileId: string, certificationId: string): Promise<CommandResult> {
    try {
      const result = await axios.request<CommandResult>({
        url: `${this._apiBaseUrl}/deleteCertification`,
        data: { personProfileId: profileId, id: certificationId },
        method: 'DELETE'
      });
      return result?.data;
    } catch (e) {
      const axiosError = e as AxiosError<CommandResult>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async createAvailabilitySlot(profileId: string, body: CreateAvailabilitySlotBody): Promise<ItemCommandResult<AvailabilitySlotDto>> {
    try {
      return await this.post(body, `${encodeURIComponent(profileId)}/availabilitySlots`);
    } catch (e) {
      const axiosError = e as AxiosError<ItemCommandResult<AvailabilitySlotDto>>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async deleteAvailabilitySlot(profileId: string, slotId: string, reason?: string): Promise<CommandResult> {
    try {
      return await this.delete(slotId, `${encodeURIComponent(profileId)}/availabilitySlots`, { reason });
    } catch (e) {
      const axiosError = e as AxiosError<CommandResult>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async updateAvailabilitySlotTitle(profileId: string, slotId: string, newTitle: string): Promise<CommandResult> {
    try {
      return await this.put({ newTitle }, `${encodeURIComponent(profileId)}/availabilitySlots/${encodeURIComponent(slotId)}/updateTitle`);
    } catch (e) {
      const axiosError = e as AxiosError<CommandResult>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async updateAvailabilitySlotDisabledValue(profileId: string, slotId: string, isDisabled: boolean): Promise<CommandResult> {
    try {
      return await this.put({ isDisabled }, `${encodeURIComponent(profileId)}/availabilitySlots/${encodeURIComponent(slotId)}/updateDisabledValue`);
    } catch (e) {
      const axiosError = e as AxiosError<CommandResult>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async updateShowProfileImagePrivacySetting(personProfileId: string, showProfileImage: boolean): Promise<CommandResult> {
    try {
      return await this.put({ showProfileImage, personProfileId }, `updateShowProfileImagePrivacySetting`);
    } catch (e) {
      const axiosError = e as AxiosError<CommandResult>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async updateShowEnterpriseNameOnProfessionalExperiencesPrivacySetting(
    personProfileId: string,
    showEnterpriseNameOnProfessionalExperiences: boolean
  ): Promise<CommandResult> {
    try {
      return await this.put(
        { showEnterpriseNameOnProfessionalExperiences, personProfileId },
        `updateShowEnterpriseNameOnProfessionalExperiencesPrivacySetting`
      );
    } catch (e) {
      const axiosError = e as AxiosError<CommandResult>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async upsertMeetingAvailabilitySlot(
    profileId: string,
    body: UpsertMeetingAvailabilitySlotBody
  ): Promise<ItemCommandResult<MeetingAvailabilitySlotDto>> {
    try {
      return await this.put(body, `${encodeURIComponent(profileId)}/meetingAvailabilitySlot`);
    } catch (e) {
      const axiosError = e as AxiosError<ItemCommandResult<MeetingAvailabilitySlotDto>>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async updatePrivacySettings(
    userNameVisible: boolean,
    userImageVisible: boolean,
    userUrlVisible: boolean,
    displayEnterpriseNameOnExperiences: boolean
  ): Promise<CommandResult> {
    try {
      return await this.put(
        {
          userNameVisible,
          userImageVisible,
          userUrlVisible,
          displayEnterpriseNameOnExperiences
        },
        'updatePrivacySettings'
      );
    } catch (e) {
      const axiosError = e as AxiosError<CommandResult>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async updateAvailabilityNotificationsConfig(
    personId: string,
    availabilitySlotId: string,
    notificationsFrequency: AvailabilitySlotNotificationFrequency
  ): Promise<CommandResult> {
    try {
      return await this.put(
        { notificationsFrequency },
        `${encodeURIComponent(personId)}/AvailabilitySlots/${encodeURIComponent(availabilitySlotId)}/notifications`
      );
    } catch (e) {
      const axiosError = e as AxiosError<CommandResult>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async updateReceiveEmails(userId: string, receiveEmails: boolean): Promise<CommandResult> {
    try {
      return await this.put<{ receiveEmails: boolean }, CommandResult>({ receiveEmails }, `${encodeURIComponent(userId)}/ReceiveEmails`);
    } catch (e) {
      const axiosError = e as AxiosError<CommandResult>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async upsertAccountCancellationRequest(userId: string, body: { reason: PersonCancelAccountReason; comment: string }) {
    try {
      return await this.put<{ reason: PersonCancelAccountReason; comment: string }, CommandResult>(
        body,
        `${encodeURIComponent(userId)}/UpsertAccountCancellationRequest`
      );
    } catch (e) {
      const axiosError = e as AxiosError<CommandResult>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async markProfileSectionAsCompleted(personProfileId: string, profileSection: ProfileSection): Promise<CommandResult> {
    try {
      return await this.put<{ profileSection: ProfileSection }, CommandResult>(
        { profileSection },
        `${encodeURIComponent(personProfileId)}/MarkProfileSectionAsCompleted`
      );
    } catch (e) {
      const axiosError = e as AxiosError<CommandResult>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  private async getPresignedUrl(profileId: string, data: UpsertPersonCvBody): Promise<ItemCommandResult<UpsertPersonCvDocumentsResponse>> {
    const { fileInfo } = splitFileInfo(data?.cvDocumentFile ?? undefined);

    try {
      return await this.put(
        {
          cvDocumentFile: fileInfo,
          cvDocumentFileSize: fileInfo?.size,
          cvDocumentFileChanged: data.cvDocumentChanged
        },
        `${encodeURIComponent(profileId)}/upsertCvDocument`
      );
    } catch (e) {
      const axiosError = e as AxiosError<ItemCommandResult<UpsertPersonCvDocumentsResponse>>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async autofillFromLinkedin(profileId: string, linkedInUrl: string): Promise<ItemCommandResult<{ jobId: string }>> {
    try {
      return await this.put<{ linkedInUrl: string }, ItemCommandResult<{ jobId: string }>>(
        { linkedInUrl },
        `${encodeURIComponent(profileId)}/enqueueAutofillFromLinkedin`
      );
    } catch (e) {
      const axiosError = e as AxiosError<ItemCommandResult<{ jobId: string }>>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async autofillFromCv(profileId: string, fileId: string): Promise<ItemCommandResult<{ jobId: string }>> {
    try {
      return await this.put<{ fileId: string }, ItemCommandResult<{ jobId: string }>>(
        { fileId: fileId },
        `${encodeURIComponent(profileId)}/enqueueAutofillFromCv`
      );
    } catch (e) {
      const axiosError = e as AxiosError<ItemCommandResult<{ jobId: string }>>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async upsertCvDocument(profileId: string, data: UpsertPersonCvBody): Promise<ItemCommandResult<{ jobId: string }>> {
    try {
      if (!data.cvDocumentChanged)
        return {
          item: undefined as any,
          isSuccess: true,
          error: '',
          identity: ''
        };

      const response = await this.getPresignedUrl(profileId, data);

      if (!response.isSuccess) {
        return {
          item: undefined as any,
          isSuccess: false,
          error: i18n.t('Error subiendo los ficheros'),
          identity: ''
        };
      }

      const filesToUpload = [
        {
          file: data?.cvDocumentFile?.file,
          url: response.item.cvDocumentFileUrl,
          fileData: data?.cvDocumentFile,
          fileId: response.item.cvDocumentFileId
        }
      ].filter((x) => x.url != null && x.file != null);

      if (filesToUpload?.length === 0) {
        return {
          item: undefined as any,
          isSuccess: false,
          error: i18n.t('Error subiendo los ficheros'),
          identity: ''
        };
      }

      const { failedUploads } = await uploadFiles(filesToUpload.map(({ file, url }) => ({ file: file!, presignedUrl: url })));

      if (failedUploads?.length > 0) {
        return {
          item: undefined as any,
          isSuccess: false,
          error: i18n.t('Error subiendo los ficheros'),
          identity: ''
        };
      }

      filesToUpload.forEach(({ fileData, fileId }) => {
        if (fileData) {
          fileData.id = fileId;
        }
      });

      return await this.autofillFromCv(profileId, filesToUpload[0].fileId);
    } catch (e) {
      const axiosError = e as AxiosError<ItemCommandResult<{ jobId: string }>>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }

  public async confirmAvailability(personProfileId: string): Promise<CommandResult> {
    try {
      return await this.put<null, CommandResult>(null, `${encodeURIComponent(personProfileId)}/ConfirmAvailability`);
    } catch (e) {
      const axiosError = e as AxiosError<CommandResult>;
      if (axiosError?.response?.data == null) throw e;
      return axiosError.response.data;
    }
  }
}
